import {
  type FC, useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { type ModalProps } from '~/modal/modalType.enum';
import { useBoundarySelectEditCustomBoundary } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/useBoundarySelectEditCustomBoundary';
import {
  checkBoundaryIsCustom,
  useCustomBoundaryGroupsSelector,
  useGetBoundaryGroup,
  useGlobalBoundaryGroups,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useMapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import { useBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { boundaryDrawAdvancedEditOpenModal } from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.actionCreators';
import { mapSettingsBoundariesClearIsolatedBoundaries } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import {
  boundarySelectEditCloseModal, boundarySelectEditCustomBoundaryOpenModal,
} from '../../../store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import { clearBoundaryLocationsFilter } from '../../../store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import {
  BoundaryEditModalComponent, type BoundaryEditResults,
} from './boundaryEditModal.component';
import {
  BoundaryEditAction, BoundaryEditManualMethod, BoundaryEditSelectFrom,
} from './boundaryEditModal.helpers';

export type BoundaryEditModalContainerProps = ModalProps<{
  boundaryTerritoryGroupId: number;
  boundaryId?: number;
}>;

export const BoundaryEditModalContainer: FC<BoundaryEditModalContainerProps> = (props) => {
  const boundaryTerritoryGroups = useBoundaryTerritoryGroupsSelector();
  const boundaryTerritoryGroup = boundaryTerritoryGroups
    .find(group => group.boundaryTerritoryGroupId === props.boundaryTerritoryGroupId);
  const dispatch = useDispatch();
  const globalBoundaryGroups = useGlobalBoundaryGroups();
  const customBoundaryGroups = useCustomBoundaryGroupsSelector();
  const boundaryItems = useMapBoundariesSelector();
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const { onBoundarySelectEditCustomBoundarySubmit } = useBoundarySelectEditCustomBoundary();

  const isAdvancedEdit = props.boundaryId !== undefined;

  const editedBoundaryGroup = useMemo(() => getBoundaryGroupById(boundaryTerritoryGroup?.boundaryGroupId),
    [boundaryTerritoryGroup, getBoundaryGroupById]);

  const defaultBoundaryGroup = useMemo(() => {
    if (!boundaryTerritoryGroup) {
      return undefined;
    }

    const preferredBG = boundaryTerritoryGroup.settings.selectionEditPreferredBoundaryGroup?.id;
    const originBG = editedBoundaryGroup?.createdFrom?.boundaryGroupIds?.[0];

    const defaultBgId = preferredBG ?? originBG;
    const defaultBg = getBoundaryGroupById(defaultBgId);

    return defaultBg?.visible ? defaultBg : undefined;
  }, [boundaryTerritoryGroup, editedBoundaryGroup?.createdFrom?.boundaryGroupIds, getBoundaryGroupById]);

  const isDefaultBoundaryGroupCustom = checkBoundaryIsCustom(defaultBoundaryGroup);

  const defaultManualMethod = useMemo(() => {
    if (defaultBoundaryGroup !== undefined) {
      return BoundaryEditManualMethod.Selection;
    }

    if (isAdvancedEdit) {
      return BoundaryEditManualMethod.Draw;
    }

    return undefined;
  }, [defaultBoundaryGroup, isAdvancedEdit]);

  const defaultSelectFrom = useMemo(() => {
    if (!defaultBoundaryGroup) {
      return undefined;
    }

    return isDefaultBoundaryGroupCustom ? BoundaryEditSelectFrom.Territory : BoundaryEditSelectFrom.Boundary;
  }, [defaultBoundaryGroup, isDefaultBoundaryGroupCustom]);

  const defaultBoundaryGroupParams = useMemo(() => {
    if (!defaultBoundaryGroup) {
      return undefined;
    }

    return isDefaultBoundaryGroupCustom ? ({
      custom: defaultBoundaryGroup?.id,
    }) : ({
      global: defaultBoundaryGroup?.id,
    });
  }, [defaultBoundaryGroup, isDefaultBoundaryGroupCustom]);

  const onSubmit = useCallback((results: BoundaryEditResults) => {
    if (!boundaryTerritoryGroup) {
      return;
    }

    dispatch(clearBoundaryLocationsFilter());
    dispatch(mapSettingsBoundariesClearIsolatedBoundaries());

    if (results.manualMethod === BoundaryEditManualMethod.Selection) {
      const selectedBoundaryGroup = getBoundaryGroupById(results.selectedBoundaryGroupId);
      if (!selectedBoundaryGroup) {
        return;
      }

      dispatch(boundarySelectEditCustomBoundaryOpenModal({
        actionType: results.action,
        boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
        selectBoundaryGroupId: selectedBoundaryGroup.id,
        boundaryId: props.boundaryId,
        onClose: () => {
          dispatch(boundarySelectEditCloseModal());
        },
        onSubmit: (selectState) => {
          onBoundarySelectEditCustomBoundarySubmit(selectState);
        },
      }));
    }

    if (results.manualMethod === BoundaryEditManualMethod.Draw) {
      dispatch(boundaryDrawAdvancedEditOpenModal({
        boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
        boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
        action: results.action,
        boundaryId: props.boundaryId,
      }));
    }

    const onClose = props.onClose;
    onClose();
  }, [boundaryTerritoryGroup, dispatch, getBoundaryGroupById, onBoundarySelectEditCustomBoundarySubmit, props.boundaryId, props.onClose]);

  if (!boundaryTerritoryGroup || !checkBoundaryIsCustom(editedBoundaryGroup)) {
    return null;
  }

  const boundaryGroupIsEmpty = !boundaryItems.get(boundaryTerritoryGroup.boundaryGroupId)?.size;

  return (
    <BoundaryEditModalComponent
      isOpen={props.isOpen}
      onClose={props.onClose}
      onSubmit={onSubmit}
      boundaryGroupIsEmpty={boundaryGroupIsEmpty}
      globalBoundaryGroups={globalBoundaryGroups}
      customBoundaryGroups={customBoundaryGroups}
      defaultAction={isAdvancedEdit ? BoundaryEditAction.EditBoundaries : undefined}
      defaultManualMethod={defaultManualMethod}
      defaultSelectFrom={defaultSelectFrom}
      defaultBoundaryGroup={defaultBoundaryGroupParams}
    />
  );
};
